import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaProjectDiagram } from 'react-icons/fa';
import { RiHandCoinLine } from 'react-icons/ri';
import { v4 as uuidv4 } from 'uuid';

import BreadCrumb from '~/components/BreadCrumb';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import ExitButton from '~/components/Buttons/Exit';
import NoticeDialog from '~/components/Dialogs/Notice';
import ProjectInfo from '~/components/ProjectInfo/Default';
import SaveButton from '~/components/Buttons/Save';
import TextArea from '~/components/TextArea';
import UploadCompletionFiles from '~/components/UploadCompletionFiles';
import UploadFiles from '~/components/UploadFiles';

import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Content, Wrapper } from './styles';

interface IFormProcedure3 {
  record_note: string;
}

interface ILocation {
  id: string;
  type: string;
  code: string;
  status: string;
  project_procedure_id: string;
}

const Procedure3: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const projectTemp = useRef(uuidv4());
  const projectCompletionTemp = useRef(uuidv4());
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOpenNotice, setIsOpenNotice] = useState(false);

  const { handleSubmit, register, errors } = useForm<IFormProcedure3>();

  useEffect(() => {
    if (state === undefined) {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Solicitação não localizada',
      });

      history.push('/projects/rebate');
    } else if (state.status.toUpperCase() === 'CANCELADO') {
      addToast({
        type: 'error',
        title: 'Operação inválida!',
        description: 'Solicitação Cancelada',
      });

      history.push('/projects/rebate');
    }
  }, [addToast, history, state, user]);

  const submitForm = useCallback(
    async (data: IFormProcedure3) => {
      try {
        setIsSaving(true);

        await api.put(`/projects/${state.id}`, {
          ...data,
          code: state.code,
          project_procedure_id: state.project_procedure_id,
          project_temp_id: projectTemp.current,
          project_completion_temp_id: projectCompletionTemp.current,
          record_description: 'Envio de comprovação de uso da verba',
          type: 'REBATE',
          validation_type: 'APROVADO',
        });

        addToast({
          type: 'success',
          title: 'Solicitação atualizada!',
          description: 'Solicitação atualizada com sucesso.',
        });

        history.push('/projects/rebate');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, projectCompletionTemp, projectTemp, state],
  );

  const onExit = () => {
    api.delete(`projectfilesreceived/projecttempid/${projectTemp.current}`);
    api.delete(
      `projectcompletionfilesreceived/` +
        `projectcompletiontempid/${projectCompletionTemp.current}`,
    );

    history.push('/projects/rebate');
  };

  const handleFiles = (): React.ReactNode | null => {
    if (user.user_type.type !== 'REP') {
      return (
        <Content>
          <UploadFiles
            type="anexo"
            project_temp_id={projectTemp.current}
            project_type="REBATE"
            sequence={3}
            user_id={user.id}
          />
        </Content>
      );
    }

    return null;
  };

  return (
    <Container>
      <BreadCrumb
        main="Solicitações"
        path="/projects/main"
        firstChildPath="/projects/rebate"
        firstChild="Rebate"
        secondChild="Atualização Solicitação"
        mainIcon={FaProjectDiagram}
        firstChildIcon={RiHandCoinLine}
        secondChildIcon={RiHandCoinLine}
        mainColored
        firstChildColored
      />

      <ProjectInfo project_id={state.id} />

      <form onSubmit={handleSubmit(submitForm)}>
        <Content>
          <UploadCompletionFiles
            type="conclusao"
            project_completion_temp_id={projectCompletionTemp.current}
            project_type="REBATE"
            sequence={3}
            user_id={user.id}
            drag_message="Arraste os arquivos de comprovação aqui..."
          />
        </Content>

        <Content>
          <Wrapper style={{ width: '100%' }}>
            <TextArea
              name="record_note"
              labelFor="record_note"
              labelText="Observação"
              error={errors?.record_note?.message}
              ref={register}
              maxLength={1000}
            />
          </Wrapper>
        </Content>

        {handleFiles()}

        <div
          style={{
            marginTop: 20,
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              api
                .get(
                  `/projectcompletionfilesreceived/projectcompletiontemp?` +
                    `project_completion_temp_id=${projectCompletionTemp.current}`,
                )
                .then(response => {
                  if (response.data.length > 0) {
                    setIsOpenConfirmProcedure(true);
                  } else {
                    setIsOpenNotice(true);
                  }
                });
            }}
          />

          <ExitButton
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title={`Confirma envio da solicitação ${state.code}?`}
        subTitle="A solicitação será enviada para o próximo procedimento"
        opConfirm="Sim"
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />

      <NoticeDialog
        isOpen={isOpenNotice}
        type="error"
        title="Necessário anexar arquivos de comprovação!"
        subTitle={
          `Não é possível enviar a solicitação sem anexar ` +
          `arquivos de comprovação de uso da verba`
        }
        setIsOpen={setIsOpenNotice}
      />
    </Container>
  );
};

export default Procedure3;
